import React from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';

export interface AdminLoadMoreProps {
  currentCount: number;
  totalCount: number;
  itemName?: string;
  onLoadMore: () => void;
  step?: number;
  onShowAll?: () => void;
  isLoading?: boolean;
}

export const AdminLoadMore: React.FC<AdminLoadMoreProps> = ({
  currentCount,
  totalCount,
  itemName = 'سجل',
  onLoadMore,
  step = 10,
  onShowAll,
  isLoading = false,
}) => {
  if (totalCount <= 0) return null;

  const hasMore = currentCount < totalCount;
  const remaining = Math.max(0, totalCount - currentCount);
  const nextIncrement = Math.min(step, remaining);
  const percent = Math.min(100, Math.round((currentCount / totalCount) * 100));

  return (
    <div
      style={{
        marginTop: '1.25rem',
        padding: '1rem 1.25rem',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.75rem',
        direction: 'rtl',
      }}
    >
      {/* Progress Counter Row */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
          fontSize: '12px',
          color: '#64748b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              fontWeight: '700',
              color: '#0f172a',
              background: '#f1f5f9',
              padding: '3px 10px',
              borderRadius: '999px',
            }}
          >
            عرض <strong style={{ color: '#E5232A' }}>{currentCount}</strong> من أصل{' '}
            <strong style={{ color: '#0f172a' }}>{totalCount}</strong> {itemName}
          </span>
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>({percent}%)</span>
        </div>

        {hasMore && (
          <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '600' }}>
            متبقي {remaining} {itemName} غير معروض
          </span>
        )}
      </div>

      {/* Progress Bar Line */}
      <div
        style={{
          width: '100%',
          height: '4px',
          background: '#f1f5f9',
          borderRadius: '999px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            background: hasMore ? 'linear-gradient(90deg, #E5232A, #f97316)' : '#10b981',
            borderRadius: '999px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Action Buttons Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: '0.25rem',
        }}
      >
        {hasMore ? (
          <>
            <button
              type="button"
              onClick={onLoadMore}
              disabled={isLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 24px',
                background: 'linear-gradient(135deg, #E5232A 0%, #C41219 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(229, 35, 42, 0.25)',
                transition: 'all 0.2s ease',
              }}
            >
              <ChevronDown size={16} />
              <span>المزيد (+{nextIncrement} {itemName})</span>
            </button>

            {onShowAll && totalCount > currentCount && (
              <button
                type="button"
                onClick={onShowAll}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 16px',
                  background: '#f8fafc',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                <span>عرض الكل ({totalCount})</span>
              </button>
            )}
          </>
        ) : (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: '#059669',
              background: '#ecfdf5',
              padding: '6px 16px',
              borderRadius: '999px',
              border: '1px solid #a7f3d0',
              fontSize: '12px',
              fontWeight: '700',
            }}
          >
            <CheckCircle2 size={15} />
            <span>تم استعراض كافة السجلات بالكامل ({totalCount} {itemName})</span>
          </div>
        )}
      </div>
    </div>
  );
};
